<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script>
import LogoutApi from '@/api/logout'
import authUtils from '@/utils/auth'
export default {
  name: 'App',
  created() {
    // 动态加载字体文件
    this.loadFont()
    // If user navigates to /logout, perform backend logout and clear local auth
    // This handles frontend-initiated logout flows
    if (this.$route && this.$route.path === '/logout') {
      LogoutApi.logout().catch(() => {
        // ignore backend logout errors, still clear local auth
      }).finally(() => {
        authUtils.clearAuth()
        this.$router.replace('/')
      })
    }
      // 禁止通过	ctrl + +/- 和 	ctrl + 滚轮 对页面进行缩放
      document.addEventListener('keydown', function (event) {
          if ((event.ctrlKey === true || event.metaKey === true) &&
              (event.which === 61 || event.which === 107 ||
                  event.which === 173 || event.which === 109 ||
                  event.which === 187 || event.which === 189)) {
              event.preventDefault()
          }
      }, false)
      // Chrome IE 360
      window.addEventListener('mousewheel', function (event) {
          if (event.ctrlKey === true || event.metaKey) {
              event.preventDefault()
          }
      }, {
          passive: false
      })

      // firefox
      window.addEventListener('DOMMouseScroll', function (event) {
          if (event.ctrlKey === true || event.metaKey) {
              event.preventDefault()
          }
      }, {
          passive: false
      })


  },
  methods: {
    async loadFont() {
      // 获取 OSS CDN 地址，如果未配置则使用本地文件
      const ossCdn = process.env.VUE_APP_OSS_CDN || ''
      let fontUrl
      let useProxy = false
      
      if (ossCdn) {
        // 开发环境和生产环境都使用代理避免 CORS 问题
        // 开发环境：使用 webpack dev server 代理
        // 生产环境：使用 Express 服务器代理
        fontUrl = `/oss-resource/font/Pin.ttf`
        useProxy = true

        // 先尝试加载 OSS 字体，如果失败则回退到本地
        await this.loadFontFromUrl(fontUrl, useProxy)
      } else {
        // 直接使用本地文件
        this.loadLocalFont()
      }
    },
    
    async loadFontFromUrl(fontUrl, useProxy = false) {
      return new Promise((resolve) => {
        // 测试字体文件是否可以加载
        const link = document.createElement('link')
        link.rel = 'preload'
        link.as = 'font'
        link.type = 'font/ttf'
        // 对于字体资源，始终设置 crossorigin 属性（即使是代理，浏览器也需要匹配 credentials 模式）
        // 使用 'anonymous' 表示不需要 credentials，这样可以正确预加载字体
        link.crossOrigin = 'anonymous'
        link.href = fontUrl
        
        const timeout = setTimeout(() => {
          console.warn('字体文件加载超时，回退到本地文件')
          link.remove()
          this.loadLocalFont()
          resolve()
        }, 5000) // 5秒超时
        
        link.onerror = (error) => {
          clearTimeout(timeout)
          console.warn('字体文件加载失败:', error)
          console.warn('可能的原因：1. OSS 文件权限不是公共读 2. CORS 配置问题 3. 文件不存在')
          console.warn('正在回退到本地字体文件...')
          // 回退到本地文件
          this.loadLocalFont()
          resolve()
        }
        
        link.onload = () => {
          clearTimeout(timeout)
          console.log('字体文件从 OSS 加载成功')
          // 加载成功，创建 @font-face
          this.createFontFace(fontUrl)
          resolve()
        }
        
        document.head.appendChild(link)
      })
    },
    
    loadLocalFont() {
      // 加载本地字体文件
      try {
        const localFontUrl = require('@/assets/font/Pin.ttf')
        this.createFontFace(localFontUrl)
        console.log('已使用本地字体文件')
      } catch (e) {
        console.error('无法加载本地字体文件:', e)
        // 最后的回退方案：使用系统字体
        console.warn('将使用系统默认字体')
      }
    },
    
    createFontFace(fontUrl) {
      // 检查是否已经创建过
      const existingStyle = document.getElementById('pingfang-font-face')
      if (existingStyle) {
        existingStyle.remove()
      }
      
      // 动态创建 @font-face 样式
      const style = document.createElement('style')
      style.id = 'pingfang-font-face'
      style.textContent = `
        @font-face {
          font-family: 'PingFang Light';
          src: url('${fontUrl}') format('truetype');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
      `
      document.head.appendChild(style)
    }
  }
}
</script>

<style>
/* 字体通过 JavaScript 动态加载，移除了静态 @font-face */

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

#app {
  font-family: 'PingFang Light', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  width: 100%;
  height: 100vh;
  overflow-x: hidden; /* 防止水平滚动 */
}

html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  background: #1e3c72;
  overflow-x: hidden; /* 防止水平滚动 */
}

/* 移动端全局优化 */
@media (max-width: 768px) {
  html {
    /* 防止iOS双击缩放 */
    touch-action: manipulation;
    /* 优化滚动性能 */
    -webkit-overflow-scrolling: touch;
  }

  body {
    /* 防止iOS橡皮筋效果 */
    overscroll-behavior: contain;
    /* 防止文本选择 */
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  /* 允许输入框和可编辑元素选择文本 */
  input, textarea, [contenteditable] {
    -webkit-user-select: text;
    -moz-user-select: text;
    -ms-user-select: text;
    user-select: text;
  }

  /* 优化触摸目标大小 */
  button, a, input, select, textarea {
    min-height: 44px;
    min-width: 44px;
  }

  /* 防止iOS输入框缩放 */
  input[type="text"],
  input[type="email"],
  input[type="number"],
  input[type="password"],
  textarea {
    font-size: 16px;
  }

  /* 优化按钮触摸反馈 */
  button:active {
    transform: scale(0.98);
    transition: transform 0.1s ease;
  }

  /* 优化链接触摸反馈 */
  a:active {
    opacity: 0.7;
    transition: opacity 0.1s ease;
  }
}

/* 超小屏幕优化 */
@media (max-width: 320px) {
  #app {
    font-size: 14px;
  }

  /* 进一步优化触摸目标 */
  button, a, input, select, textarea {
    min-height: 40px;
    min-width: 40px;
  }
}

/* 横屏模式优化 */
@media (max-width: 768px) and (orientation: landscape) {
  #app {
    height: 100vh;
    max-height: 100vh;
  }

  body {
    height: 100vh;
    max-height: 100vh;
  }
}

/* 高DPI屏幕优化 */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  #app {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
}

/* 暗色模式支持 */
@media (prefers-color-scheme: dark) {
  html, body {
    background: #1e3c72;
  }
}

/* 减少动画（用户偏好） */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
